import React, { useState, useEffect } from 'react';
import { FaPlus, FaRegTrashAlt, FaPencilAlt, FaEye } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { Input, message, Popconfirm } from 'antd';
import { normalizeString, getSuccessMessageByModule } from '~/Utils';

import api from '~/services/api';
import errorHandler from '~/Utils/errorHandler';

import PageTitle from '~/components/PageTitle';
import Button from '~/components/Button';
import { TableHeader, Table, TableActions } from '~/components/Table';
import Box from '~/components/Box';
import Can from '~/components/Can';
import Cannot from '~/components/Cannot';

import DefaultLayout from '../_layouts/full';
import SpeechForm from './form';
import { Global } from './styles';

export default function PaymentLot() {
  const [loading, setLoading] = useState(false);
  const [recordData, setRecordData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [searchTerm, setSearchTerm] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const { t } = useTranslation();

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/areas');
      setRecordData(data);
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  };

  const handleNew = () => {
    setShowForm(true);
  };

  const handleEdit = (id) => {
    setSelectedRecord(id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await api.delete(`/areas/${id}`);
      message.success(getSuccessMessageByModule(t('screens:areas.titleSingular'), t('messages:successDeletedFem')));
      await fetchRecords();
    } catch (error) {
      const response = error && error.response && error.response.data;
      if (response) {
        const { message: msg } = JSON.parse(response);
        message.warning(msg);
      }
      errorHandler(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!showForm) {
      setSelectedRecord(null);
      fetchRecords();
    }
  }, [showForm]);

  useEffect(() => {
    fetchRecords();
  }, []);

  useEffect(() => {
    if (searchTerm && searchTerm.trim() !== '') {
      const results = recordData.filter(
        (item) =>
          item.name.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1 ||
          normalizeString(item.name).toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1
      );
      setTableData(results);
    } else {
      setTableData(recordData);
    }
  }, [recordData, searchTerm]);

  const tableColumns = [
    {
      title: t('screens:areas.data.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text) => text.toUpperCase(),
    },
    {
      title: t('screens:areas.data.sub_areas'),
      dataIndex: 'sub_areas',
      key: 'sub_areas',
      render: (text, record) =>
        record.sub_areas.map(
          (area, i, data) => `${area && area.name.toUpperCase()} ${data.length - 1 !== i ? ', ' : ''} `
        ),
    },
    {
      title: t('screens:areas.data.actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: '140px',
      align: 'center',
      render: (text, record) => (
        <TableActions>
          <Can permission="@area/edit">
            <Button size="small" title="Editar" onClick={() => handleEdit(record.id)}>
              <FaPencilAlt />
            </Button>
          </Can>
          <Cannot permission="@area/edit">
            <Can permission="@area/view">
              <Button style={{ margin: 'auto' }} size="small" title="Visualizar" onClick={() => handleEdit(record.id)}>
                <FaEye />
              </Button>
            </Can>
          </Cannot>
          <Can permission="@area/delete">
            <Popconfirm
              title={t('messages:confirmDelete')}
              okText={t('messages:yes')}
              onConfirm={() => handleDelete(record.id)}
              cancelText={t('messages:no')}
            >
              <Button size="small" title="Excluir">
                <FaRegTrashAlt />
              </Button>
            </Popconfirm>
          </Can>
        </TableActions>
      ),
    },
  ];

  return (
    <DefaultLayout>
      <PageTitle title={t('screens:areas.title')}>
        <Can permission="@area/edit">
          <Button loading={loading} color="primary" onClick={handleNew}>
            <FaPlus />
            {t('screens:areas.btnNew')}
          </Button>
        </Can>
      </PageTitle>
      <Box>
        <TableHeader>
          <Input.Search onSearch={(value) => setSearchTerm(value)} className="search-field" />
        </TableHeader>
        <Table rowKey="id" loading={loading} dataSource={tableData} columns={tableColumns} />

        <Global />

        <SpeechForm visible={showForm} areaID={selectedRecord} onClose={() => setShowForm(false)} />
      </Box>
    </DefaultLayout>
  );
}
